import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AddEditCommitted } from "./lazyForms";
import CommonDataView from "./CommonDataView";
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import FloatingDownloadButton from "./FloatingDownloadButton";
import { formatUGX } from "./UtilityFunctions";

function Committed({ cars, setCars, initialSelectedRow = "" , isLoading = false }){
    const ownSheetName = "Committed";

    const {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(cars, setCars);

    return (
        <div className="flex flex-col h-full sheet-viewport" id="committed-content">
            <Content isLoading={isLoading}
                modalVisible={modalVisible}
                setModalVisibility={setModalVisibility}
                filteredDataArray={filteredDataArray} setCars={setCars} sortData={sortData}
                sortOrder={sortOrder} parentSheetName={ownSheetName}
                filterData={filterData} filterObject={filterObject}
                ownSheetName={ownSheetName}
                initialSelectedRow={initialSelectedRow}
            />
            <Summary setModalVisibility={setModalVisibility} filteredDataArray={filteredDataArray} />
        </div>
    )
}

function Content({ filteredDataArray, modalVisible, setModalVisibility,
  sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, initialSelectedRow = "", isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Make', 'Model', 'ModelYear', 'CustomerName', 'CommitmentDate']);
    const [ currentVehicleIndex, setCurrentVehicle ] = useState();
    const openModalEdit = (vehicleId)=>{
        setCurrentVehicle(vehicleId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }


    const [ selectedRow, setSelectedRow ] = useState(initialSelectedRow)

    useEffect(() => {
        if (initialSelectedRow) setSelectedRow(initialSelectedRow);
    }, [filteredDataArray, initialSelectedRow]);

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    // Closures reference no component state — stable for the component lifetime.
    const formattingRules = useMemo(() => ({
        Days: {
            formatByValue: (val)=>{
                if(val > 240 && val <= 270){
                    return {color: "orange", fontWeight: "bold"}
                }
                if(val > 270){
                    return {color: "red", fontWeight: "bold"}
                }
                return {color: "green", fontWeight: "bold"}
            }
        },
        DaysCommitted: {
            formatByValue: (val)=>{
                if(val > 30 && val <= 60){
                    return {color: "orange", fontWeight: "bold", textAlign: "center"}
                }
                if(val > 60){
                    return {color: "red", fontWeight: "bold", textAlign: "center"}
                }
                return {color: "green", fontWeight: "bold", textAlign: "center"}
            }
        }
    }), []);

    // Row colors derived purely from row data — no captured state.
    const rowFormattingRule = useCallback((row) => {
        if(row.DaysCommitted > 60) return { backgroundColor: "#fee2e2" }  // light red
        if(row.DaysCommitted > 30) return { backgroundColor: "#ffedd5" }  // light orange
        return undefined
    }, []);

    //The sold/reserved backfill wizard used to launch from here. It moved to
    //Settings → Bring in your records: it is a once-per-lifetime migration task,
    //and a standing button on the sheet people work deals in every day invited
    //using it instead of Inventory → Sell. See import/BringInRecords.jsx.

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"VehicleID"}
                formattingRules={formattingRules}
                rowFormattingRule={rowFormattingRule}
                searchPlaceholder="Search committed…"
            />
            {
                modalVisible.visible &&  filteredDataArray[currentVehicleIndex] &&
                <AddEditCommitted 
                    formMode={modalVisible.mode} 
                    showModal={setModalVisibility} 
                    car={filteredDataArray[currentVehicleIndex]} 
                    parentSheetName={parentSheetName} 
                    //In add mode, read the origin that the user selected. In edit mode, read the origin from the record's origin field
                    origin={modalVisible.mode==="add" ? modalVisible.origin : filteredDataArray[currentVehicleIndex].Origin}
                />
                    
            }
            <FloatingDownloadButton 
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                filterObject={filterObject}
            />     
        </div>
    )
}

function Summary({ filteredDataArray}){
    return (
        //Stats cards overflow their band on phones — desktop only for now
        <div id="committed-summary" className="hidden md:block">
            <DealershipStatsBar filteredDataArray={filteredDataArray} />
        </div>
    )
}


import { Card, Metric, Text, Flex, Grid } from "@tremor/react";

function DealershipStatsBar({ filteredDataArray }) {
  const [stats, setStats] = useState({
    totalVehicles: 0,
    totalValue: 0,
    totalPaid: 0,
    totalBalance: 0,
    avgProfit: 0,
    expiredCommitments: 0
  });

  useEffect(() => {
    if (!filteredDataArray || filteredDataArray.length === 0) return;
    
    const totalVehicles = filteredDataArray.length;
    const totalValue = filteredDataArray.reduce((sum, car) => sum + (Number(car.AgreedPrice) || 0), 0);
    const totalPaid = filteredDataArray.reduce((sum, car) => sum + (Number(car.TotalPaid) || 0), 0);
    const totalBalance = filteredDataArray.reduce((sum, car) => sum + (Number(car.TotalBalance) || 0), 0);
    
    const profitableVehicles = filteredDataArray.filter(car => car.GrossProfit > 0);
    const avgProfit = profitableVehicles.length > 0 
      ? profitableVehicles.reduce((sum, car) => sum + (Number(car.GrossProfit) || 0), 0) / profitableVehicles.length
      : 0;
    
    const today = new Date();
    const expiredCommitments = filteredDataArray.filter(car => {
      if (!car.CommitmentDate) return false;
      const commitmentDate = new Date(car.CommitmentDate);
    return (today - commitmentDate) / (1000 * 60 * 60 * 24) > 4 && car.TotalBalance > 0; //Commitment date > 4 days ago
    }).length;
    
    setStats({
      totalVehicles,
      totalValue,
      totalPaid,
      totalBalance,
      avgProfit,
      expiredCommitments
    });
  }, [filteredDataArray]);

  //Shared formatter — see formatUGX in UtilityFunctions.jsx.
  const formatCurrency = (value) => formatUGX(value);

  return (
    <div className="bg-white rounded-md h-20 overflow-y-auto">
      <Grid numItemsSm={3} numItemsLg={6} className="gap-2 h-full">
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Total Vehicles</Text>
          <Metric className="text-lg">{stats.totalVehicles}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Total Sales</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalValue)}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Total Paid</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalPaid)}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Outstanding</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalBalance)}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500 flex items-center">
            Expired Commitments
            {stats.expiredCommitments > 0 && (
              <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </Text>
          <Metric className={`text-lg ${stats.expiredCommitments > 0 ? 'text-red-500' : ''}`}>
            {stats.expiredCommitments}
          </Metric>
        </Card>
      </Grid>
    </div>
  );
}

export default Committed;